import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api, clearPrivilegedSessionClient } from '@/lib/api';
import i18n from '@/i18n';
import { browserTimezone } from '@/lib/datetime';
import { probeSession } from '@/lib/authSessionProbe';

const AuthCtx = createContext(null);

// Display-timezone override (Home vs Local). Persisted in localStorage so
// installed PWAs remember Local after the OS kills/reopens the WebView —
// sessionStorage is wiped on every cold PWA launch. Profile/home timezone is
// never overwritten by this; logout and an explicit profile-tz save clear it.
const SESSION_TZ_KEY = 'kal_session_tz';

function readStoredSessionTz() {
  try {
    const fromLocal = localStorage.getItem(SESSION_TZ_KEY);
    if (fromLocal) return fromLocal;
    // One-time migrate from the pre-PWA sessionStorage key.
    const fromSession = sessionStorage.getItem(SESSION_TZ_KEY);
    if (fromSession) {
      localStorage.setItem(SESSION_TZ_KEY, fromSession);
      sessionStorage.removeItem(SESSION_TZ_KEY);
      return fromSession;
    }
  } catch { /* private mode / quota */ }
  return null;
}

function applyLanguage(user) {
  if (user?.language && i18n.language !== user.language) {
    i18n.changeLanguage(user.language);
  }
}

function writeStoredSessionTz(tz) {
  try {
    if (!tz) {
      localStorage.removeItem(SESSION_TZ_KEY);
      sessionStorage.removeItem(SESSION_TZ_KEY);
      return;
    }
    localStorage.setItem(SESSION_TZ_KEY, tz);
    sessionStorage.removeItem(SESSION_TZ_KEY);
  } catch { /* ignore */ }
}

export function AuthProvider({ children }) {
  const [rawUser, setRawUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consent, setConsent] = useState(null);
  const [sessionTz, setSessionTz] = useState(() => readStoredSessionTz());
  // Bumped when a sign-in or logout commits. A /auth/me that started earlier
  // must not clear or replace that session (ISSUE-0187).
  const sessionEpoch = useRef(0);

  const adoptUser = useCallback((user) => {
    sessionEpoch.current += 1;
    setRawUser(user);
    setLoading(false);
    applyLanguage(user);
  }, []);

  const fetchMe = useCallback(async () => {
    const epochAtStart = sessionEpoch.current;
    const stillCurrent = () => sessionEpoch.current === epochAtStart;
    try {
      const result = await probeSession(api, stillCurrent);
      if (!stillCurrent() || result.status === 'stale') return null;
      if (result.status === 'user' && result.user) {
        setRawUser(result.user);
        applyLanguage(result.user);
        return result.user;
      }
      setRawUser(null);
      return null;
    } finally {
      if (stillCurrent()) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // ISSUE-0145 Rev 3: always mirror live device IANA to last_detected_browser_tz
  // (including when the host taps Not now on the Local modal). Poll while the
  // tab is visible so Chrome DevTools / OS TZ changes push to Telegram without
  // a full reload — still only PATCHes when the IANA string actually changes.
  useEffect(() => {
    if (!rawUser?.id) return undefined;
    let inFlight = false;
    let lastSent = rawUser.last_detected_browser_tz || '';

    const syncDetected = () => {
      if (document.hidden) return;
      const bt = browserTimezone();
      if (!bt || bt === lastSent || inFlight) return;
      inFlight = true;
      lastSent = bt;
      api.patch('/auth/me', { last_detected_browser_tz: bt })
        .then(({ data }) => {
          setRawUser(data);
          lastSent = data?.last_detected_browser_tz || bt;
        })
        .catch(() => {
          // Allow retry on next tick if the write failed.
          lastSent = '';
        })
        .finally(() => { inFlight = false; });
    };

    syncDetected();
    const onVis = () => { if (!document.hidden) syncDetected(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', syncDetected);
    // DevTools Sensors / OS TZ can change while the tab stays focused.
    const pollId = window.setInterval(syncDetected, 10_000);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', syncDetected);
      window.clearInterval(pollId);
    };
  }, [rawUser?.id]);

  const refreshConsents = useCallback(async () => {
    if (!rawUser) { setConsent(null); return; }
    try {
      const { data } = await api.get('/me/consents');
      setConsent(data);
    } catch {
      setConsent(null);
    }
  }, [rawUser]);

  useEffect(() => {
    if (rawUser) refreshConsents();
    else setConsent(null);
  }, [rawUser, refreshConsents]);

  const login = useCallback(async (email, password) => {
    // Defense in depth: drop any prior tab's privileged MFA token before a new identity lands.
    clearPrivilegedSessionClient();
    const { data } = await api.post('/auth/login', { email, password });
    if (data.email_verification_required) {
      return { email_verification_required: true, email: data.email };
    }
    if (data.pending) {
      return { pending: true, name: data.name };
    }
    adoptUser(data.user);
    return data.user;
  }, [adoptUser]);

  const register = useCallback(async (payload) => {
    clearPrivilegedSessionClient();
    const { data } = await api.post('/auth/register', payload);
    if (data.verification_required) {
      return {
        verification_required: true,
        email_sent: data.email_sent !== false,
      };
    }
    if (data.pending) {
      return { pending: true, name: data.name };
    }
    if (!data.user) {
      return data;
    }
    adoptUser(data.user);
    return data.user;
  }, [adoptUser]);

  /** Complete federated login after OAuth redirect handoff (same-site cookie set). */
  const completeSocialExchange = useCallback(async (code) => {
    clearPrivilegedSessionClient();
    const { data } = await api.post('/auth/social/exchange', { code });
    if (data.pending) {
      return { pending: true, name: data.name };
    }
    if (!data.user) {
      throw new Error('social_exchange_failed');
    }
    adoptUser(data.user);
    return data.user;
  }, [adoptUser]);

  const logout = useCallback(async () => {
    sessionEpoch.current += 1;
    try { await api.post('/auth/logout'); } catch { /* clear local view anyway */ }
    writeStoredSessionTz(null);
    clearPrivilegedSessionClient();
    setSessionTz(null);
    setRawUser(null);
    window.location.href = '/';
  }, []);

  const updateMe = useCallback(async (updates) => {
    const payload = { ...updates };
    // An explicit profile timezone save clears any display override so the
    // newly saved home timezone takes effect immediately.
    if (payload.timezone && payload.last_browser_local_tz === undefined) {
      payload.last_browser_local_tz = '';
    }
    const { data } = await api.patch('/auth/me', payload);
    setRawUser(data);
    if (payload.timezone) {
      writeStoredSessionTz(null);
      setSessionTz(null);
    }
    if (payload.language) i18n.changeLanguage(payload.language);
    return data;
  }, []);

  // Set/clear the display timezone override. Passing the home timezone (or a
  // falsy value) clears the override rather than storing a redundant one.
  // Accepted Local → last_browser_local_tz; also refresh detected for Rev 3.
  const setSessionTimezone = useCallback((tz) => {
    const home = rawUser?.timezone;
    if (!tz || tz === home) {
      writeStoredSessionTz(null);
      setSessionTz(null);
      api.patch('/auth/me', { last_browser_local_tz: '' }).catch(() => {});
      return;
    }
    writeStoredSessionTz(tz);
    setSessionTz(tz);
    api.patch('/auth/me', {
      last_browser_local_tz: tz,
      last_detected_browser_tz: tz,
    }).catch(() => {});
  }, [rawUser]);

  const clearSessionTimezone = useCallback(() => {
    writeStoredSessionTz(null);
    setSessionTz(null);
    api.patch('/auth/me', { last_browser_local_tz: '' }).catch(() => {});
  }, []);

  // The `user` exposed to the app carries the EFFECTIVE display timezone
  // (session override if any, else home). `home_timezone` always holds the real
  // saved profile value so Profile settings can edit/display it correctly.
  const user = useMemo(() => {
    if (!rawUser) return null;
    const home = rawUser.timezone || 'Europe/Moscow';
    const effective = sessionTz || home;
    return { ...rawUser, timezone: effective, home_timezone: home };
  }, [rawUser, sessionTz]);

  const value = useMemo(() => ({
    user, loading, login, register, completeSocialExchange, logout, updateMe, refresh: fetchMe,
    consent, refreshConsents,
    sessionTimezone: sessionTz, setSessionTimezone, clearSessionTimezone,
  }), [user, loading, login, register, completeSocialExchange, logout, updateMe, fetchMe,
    consent, refreshConsents,
    sessionTz, setSessionTimezone, clearSessionTimezone]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
